/**
 * Permission Guard Component
 * Conditionally renders content based on user permissions and roles
 */

'use client';

import React from 'react';
import { useAuthGuard } from '@/domains/auth/hooks/use-auth-guard';

interface PermissionGuardProps {
  /** Component to render if user has required permissions */
  children: React.ReactNode;
  /** Required roles for access */
  requiredRoles?: string[];
  /** Required permissions for access */
  requiredPermissions?: string[];
  /** Whether admin access is required */
  requireAdmin?: boolean;
  /** Whether super admin access is required */
  requireSuperAdmin?: boolean;
  /** Fallback component to render if access is denied */
  fallback?: React.ReactNode;
  /** Whether to show fallback for non-authenticated users */
  showFallbackForUnauthenticated?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  requireAdmin = false,
  requireSuperAdmin = false,
  fallback = null,
  showFallbackForUnauthenticated = true
}) => {
  const {
    user,
    isAuthenticated,
    hasAccess,
    isAdmin,
    isSuperAdmin,
    isLoading,
    hasRole,
    hasPermission
  } = useAuthGuard({
    requiredRoles,
    requiredPermissions,
    requireAdmin,
    showLoading: false
  });

  // Show loading state
  if (isLoading) {
    return null;
  }

  // Show fallback for non-authenticated users
  if (!isAuthenticated && showFallbackForUnauthenticated) {
    return <>{fallback}</>;
  }

  // Check admin requirements
  if (requireSuperAdmin && !isSuperAdmin) {
    return <>{fallback}</>;
  }

  if (requireAdmin && !isAdmin) {
    return <>{fallback}</>;
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <>{fallback}</>;
    }
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.some(permission => hasPermission(permission));
    if (!hasRequiredPermission) {
      return <>{fallback}</>;
    }
  }

  // Check general access
  if (!hasAccess) {
    return <>{fallback}</>;
  }

  // Render protected content
  return <>{children}</>;
};

/**
 * Admin-only content guard
 */
export const AdminOnly: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback
}) => (
  <PermissionGuard requireAdmin fallback={fallback}>
    {children}
  </PermissionGuard>
);

/**
 * Super admin-only content guard
 */
export const SuperAdminOnly: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback
}) => (
  <PermissionGuard requireSuperAdmin fallback={fallback}>
    {children}
  </PermissionGuard>
);

/**
 * Role-based content guard
 */
export const RoleGuard: React.FC<{
  children: React.ReactNode;
  roles: string[];
  fallback?: React.ReactNode;
}> = ({ children, roles, fallback }) => (
  <PermissionGuard requiredRoles={roles} fallback={fallback}>
    {children}
  </PermissionGuard>
);

/**
 * Permission-based content guard
 */
export const PermissionOnly: React.FC<{
  children: React.ReactNode;
  permissions: string[];
  fallback?: React.ReactNode;
}> = ({ children, permissions, fallback }) => (
  <PermissionGuard requiredPermissions={permissions} fallback={fallback}>
    {children}
  </PermissionGuard>
);
